import { OverlijdensRisicoVerzekering } from "../../../../.generated/rapportage/rapportagetypes";
import { Column } from "../../components/BaseTable";
import { bedragFormat } from "../../../../shared/utils/currency";
import { getFormattedDateFromString } from "../../../../shared/utils/dates";
import { formatDuration } from "../../../../components/formatters";

export const getOrvColumns = ({
  overlijdensrisicoverzekering,
  isHuidig,
}: {
  overlijdensrisicoverzekering: OverlijdensRisicoVerzekering;
  isHuidig: boolean;
}): Column[] => {
  let result: Column<OverlijdensRisicoVerzekering>[] = [
    {
      header: "Overlijdensrisicoverzekering",
      accessor: () => " ",
    },
    {
      header: "Lopende of nieuwe polis",
      accessor: isHuidig ? () => "Bestaande polis" : "lopendeOfNieuwePolis",
    },
    {
      header: "Verzekeraar",
      accessor: "verzekeraar",
    },
    {
      header: "Polisnummer",
      accessor: (row) => row.polisnummer || null,
    },
    {
      header: "Ingangsdatum",
      accessor: "ingangsdatum",
      dataType: "date",
    },
    {
      header: "Einddatum verzekering, looptijd",
      accessor: (row) => {
        const einddatum = getFormattedDateFromString(row.einddatum);
        const looptijd = formatDuration(row.looptijdInMaanden);
        return looptijd ? `${einddatum}, ${looptijd}` : einddatum;
      },
    },
    {
      header: "Verzekeringnemer 1",
      accessor: (data: OverlijdensRisicoVerzekering) => {
        return data?.verzekerden && data.verzekerden.length > 0
          ? data.verzekerden[0]
          : null;
      },
    },
    {
      header: "Verzekeringnemer 2",
      accessor: (data: OverlijdensRisicoVerzekering) => {
        return data?.verzekerden && data.verzekerden.length > 1
          ? data.verzekerden[1]
          : null;
      },
    },
  ];

  if (
    overlijdensrisicoverzekering.verzekerde1Overlijdensrisicodekking ||
    overlijdensrisicoverzekering.verzekerde2Overlijdensrisicodekking
  ) {
    result.push({
      header: " ",
      accessor: () => " ",
    });
  }

  if (overlijdensrisicoverzekering.verzekerde1Overlijdensrisicodekking) {
    const columns1: Column<OverlijdensRisicoVerzekering>[] = [
      {
        header: "Verzekerde",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerden?.[0] ?? null,
        className: "bold",
      },
      {
        header: "Soort dekking",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerde1Overlijdensrisicodekking?.soort ?? null,
      },
      {
        header: "Verzekerd kapitaal",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerde1Overlijdensrisicodekking?.verzekerdKapitaalBedrag ??
          null,
        dataType: "currency",
      },
      {
        header: "Dekking daalt tot, duur daling",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerde1Overlijdensrisicodekking?.dekkingDaaltTot ?? null,
      },
      {
        header: "Premievrijstelling bij arbeidsongeschiktheid",
        accessor: (data: OverlijdensRisicoVerzekering) => {
          if (data.verzekerde1PremievrijstellingAo === null) {
            return null;
          }
          return data.verzekerde1PremievrijstellingAo ? "Ja" : "Nee";
        },
      },
      {
        header: "Partnerrente",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerde1Overlijdensrisicodekking?.partnerRente ?? null,
        dataType: "currency-with-decimals",
      },
    ];
    result = result.concat(columns1);
  }

  if (overlijdensrisicoverzekering.verzekerde2Overlijdensrisicodekking) {
    const columns2: Column<OverlijdensRisicoVerzekering>[] = [
      {
        header: "Verzekerde",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          overlijdensrisicoverzekering.verzekerde1Overlijdensrisicodekking
            ? data.verzekerden?.[1] ?? null
            : data.verzekerden?.[0] ?? null,
        className: "bold",
      },
      {
        header: "Soort dekking",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerde2Overlijdensrisicodekking?.soort ?? null,
      },
      {
        header: "Verzekerd kapitaal",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerde2Overlijdensrisicodekking?.verzekerdKapitaalBedrag ??
          null,
        dataType: "currency",
      },
      {
        header: "Dekking daalt tot, duur daling",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerde2Overlijdensrisicodekking?.dekkingDaaltTot ?? null,
      },
      {
        header: "Premievrijstelling bij arbeidsongeschiktheid",
        accessor: (data: OverlijdensRisicoVerzekering) => {
          if (data.verzekerde2PremievrijstellingAo === null) {
            return null;
          }
          return data.verzekerde2PremievrijstellingAo ? "Ja" : "Nee";
        },
      },
      {
        header: "Partnerrente",
        accessor: (data: OverlijdensRisicoVerzekering) =>
          data.verzekerde2Overlijdensrisicodekking?.partnerRente ?? null,
        dataType: "currency-with-decimals",
      },
    ];
    result = result.concat(columns2);
  }

  if (
    overlijdensrisicoverzekering.verzekerde1Overlijdensrisicodekking ||
    overlijdensrisicoverzekering.verzekerde2Overlijdensrisicodekking
  ) {
    result.push({
      header: " ",
      accessor: () => " ",
    });
  }

  const columns3: Column<OverlijdensRisicoVerzekering>[] = [
    {
      header: "(Lage) premie, betalingstermijn",
      accessor: (row) => {
        if (!row.lagePremie) {
          return null;
        }
        const bedrag = bedragFormat(row.lagePremie ?? 0, 2);
        if (row.lagePremieTermijn) {
          return `${bedrag} per ${row.lagePremieTermijn.toLowerCase()}`;
        }
        return bedrag;
      },
    },
    {
      header: "(Hoge) premie, betalingstermijn",
      accessor: (row) => {
        if (!row.hogePremie) {
          return null;
        }
        const bedrag = bedragFormat(row.hogePremie ?? 0, 2);
        if (row.hogePremieTermijn) {
          return `${bedrag} per ${row.hogePremieTermijn.toLowerCase()}`;
        }
        return bedrag;
      },
    },
    {
      header: "Einddatum premiebetaling, duur",
      accessor: (row) => {
        const einddatum = getFormattedDateFromString(
          row.einddatumPremiebetaling
        );
        const duur = formatDuration(row.premieLooptijdInMaanden);
        return einddatum + (duur ? `, ${duur}` : "");
      },
    },
    {
      header: "Polis is verpand aan geldverstrekker",
      accessor: (row) => (row.polisIsVerpandAanGeldverstrekker ? "Ja" : "Nee"),
    },
  ];

  return result.concat(columns3);
};
